/**
 * Party vehicles section (issue #2519).
 *
 * The vehicles a person or company is currently linked to. Used by both the
 * person card and the company card, the same shape as
 * `PersonCompanyLinksSection` — exactly one of `personId` / `companyId`.
 *
 * Rows come from `GET /vehicles?personId=|companyId=`, which joins
 * `vehicle_party_links` and admits only links with no `validTo` in the roles
 * owner / holder / service_orderer (#833 B4). A car the customer has sold
 * therefore drops out of the list on its own.
 *
 * `distinctVehicles` is on for the same reason as the vehicle list (#2204):
 * without it the join returns one row per link, so a customer who is both
 * owner and holder of a car would see it twice.
 */

import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { format, isValid } from 'date-fns';
import { DetailSection } from '@/components/common/DetailSection';
import { EntityListResults } from '@/components/common/EntityListResults';
import { RegistrationPlate } from '@/components/common/RegistrationPlate';
import { VehicleMakeLogo } from '@/components/common/VehicleMakeLogo';
import { useVehiclesPage, type VehicleListItem } from '@/hooks/useVehicles';
import { dateFnsLocale } from '@/utils/locale';

/** Modified and middle clicks stay with the anchor (#4108). */
function isModifiedClick(event: React.MouseEvent): boolean {
  return event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
}

export interface PartyVehiclesSectionProps {
  personId?: string;
  companyId?: string;
}

export function PartyVehiclesSection({ personId, companyId }: PartyVehiclesSectionProps) {
  const { t, i18n } = useTranslation('parties');
  const { t: tv } = useTranslation('vehicles');
  const navigate = useNavigate();

  const dateLocale = dateFnsLocale();

  // Page size and sort belong to the shared vehicle list hook (#2740).
  const query = useVehiclesPage(
    {
      ...(personId ? { personId } : {}),
      ...(companyId ? { companyId } : {}),
      // A vehicle list shows each car once, whatever roles hang off it (#2204).
      distinctVehicles: true,
    },
    {
      enabled: Boolean(personId ?? companyId),
      // Same section page size as the work-order section beside it.
      defaultRowsPerPage: 10,
      rowsPerPageOptions: [10, 25, 50],
    },
  );

  const vehicles = query.rows;

  const describe = (vehicle: VehicleListItem) =>
    [vehicle.make, vehicle.model, vehicle.year != null ? String(vehicle.year) : null]
      .filter((part) => part && part.length > 0)
      .join(' ');

  const secondary = (vehicle: VehicleListItem) => {
    const parts: string[] = [];
    if (vehicle.mileage != null) {
      parts.push(t('vehicles.mileage', { mileage: vehicle.mileage.toLocaleString(i18n.language) }));
    }
    const serviced = vehicle.lastServiceDate ? new Date(vehicle.lastServiceDate) : null;
    if (serviced && isValid(serviced)) {
      parts.push(
        t('vehicles.lastService', { date: format(serviced, 'dd.MM.yyyy', { locale: dateLocale }) }),
      );
    }
    return parts.join(' · ');
  };

  return (
    <DetailSection title={t('vehicles.title')}>
      <EntityListResults
        loading={query.isLoading}
        isError={query.isError}
        errorText={t('vehicles.loadError')}
        resultCountText={t('vehicles.resultCount', { count: query.total })}
        /* The wrapper already says it is loading; no footer for an empty list. */
        {...(query.total > 0 ? { pagination: query.pagination } : {})}
      >
        {query.isLoading ? null : vehicles.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {t('vehicles.none')}
          </Typography>
        ) : (
          <List disablePadding>
            {vehicles.map((vehicle) => (
              <ListItem key={vehicle.id} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={`/vehicles/${vehicle.id}`}
                  onClick={(event: React.MouseEvent) => {
                    if (isModifiedClick(event)) return;
                    event.preventDefault();
                    // A plain click keeps the fleet modal. A new tab opens the vehicle page.
                    navigate(`/vehicles?id=${vehicle.id}`);
                  }}
                  sx={{ color: 'inherit', textDecoration: 'none' }}
                >
                  <ListItemAvatar>
                    <VehicleMakeLogo make={vehicle.make} size={36} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <RegistrationPlate registrationNumber={vehicle.registrationNumber} />
                        <Typography variant="body2" component="span">
                          {describe(vehicle)}
                        </Typography>
                      </Stack>
                    }
                    secondary={secondary(vehicle) || undefined}
                  />
                  {vehicle.customerRole && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={tv(`roles.${vehicle.customerRole}`)}
                      sx={{ ml: 1, flexShrink: 0 }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </EntityListResults>
    </DetailSection>
  );
}
